package com.elan.edubridge.ui.staff

import com.elan.edubridge.shared.di.SharedGraph
import com.elan.edubridge.shared.domain.model.AttendanceEvent
import com.elan.edubridge.shared.domain.model.SessionState
import com.elan.edubridge.shared.domain.model.TodayDashboard
import kotlinx.browser.document
import kotlinx.coroutines.Job
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import org.w3c.dom.HTMLButtonElement

/**
 * `/app/{academyId}/today` — v4 대시보드(가이드 §3.4). [StatTiles]·[TodayClassesPanel]·
 * [AttendanceFeedPanel]을 [DashboardShell] 안에 합성한다.
 *
 * 취소된(AMEND) 이벤트 표시는 클라이언트 세션 로컬 상태([amendedEventIds])로 유지한다 —
 * `InMemoryAcademyCommandDataSource.amendAttendance`는 테스트 더블이라 실제로 이벤트 로그를
 * 갱신하지 않으므로(§S6 InMemory 데모의 알려진 한계), 재조회만으로는 "취소됨" 표시가 살아남지 않는다.
 */
object TodayDashboardPage {
    private val scope = MainScope()
    private val commandRepository = SharedGraph.createAcademyCommandRepository()
    private val getTodayDashboard = SharedGraph.createGetTodayDashboardUseCase()
    private val observeAttendanceFeed = SharedGraph.createObserveAttendanceFeedUseCase()

    private val amendedEventIds = mutableSetOf<String>()
    private var activeJob: Job? = null

    /**
     * 리뷰 M17·M18: 이전 호출이 연 `observeAttendanceFeed` 구독을 취소하지 않고 새 `launch`를
     * 또 쌓으면(저장·정정 후 새로고침 목적으로 [open]을 다시 부를 때마다) 피드 리스너가 계속
     * 누적된다 — 매번 이전 [activeJob]을 취소해 정확히 하나만 살아있게 한다.
     */
    fun open(session: SessionState, academyId: String) {
        activeJob?.cancel()
        activeJob = scope.launch {
            val dashboard = getTodayDashboard(academyId, null)
            val notifications = commandRepository.notificationSummary(academyId)

            var rendered = false
            observeAttendanceFeed(academyId, FEED_LIMIT).collect { events ->
                if (!rendered) {
                    rendered = true
                    renderFull(session, academyId, dashboard, notifications.let { if (it.isSuccess) it else null }?.let { it.deliveredCount to it.retryingCount }, events)
                } else {
                    AttendanceFeedPanel.updateInPlace(events, amendedEventIds, onCancelRequested(session, academyId))
                }
            }
        }
    }

    private fun onCancelRequested(session: SessionState, academyId: String): (AttendanceEvent) -> Unit = { event ->
        AttendanceModal.openCancelConfirm(academyId, event) {
            amendedEventIds += event.eventId
            open(session, academyId)
        }
    }

    private fun renderFull(
        session: SessionState,
        academyId: String,
        dashboard: TodayDashboard,
        delivery: Pair<Int, Int>?,
        events: List<AttendanceEvent>,
    ) {
        val deliveryHtml = delivery?.let { (delivered, retrying) ->
            """<p class="eb-delivery-status eb-meta">전달 $delivered · 재시도 $retrying</p>"""
        } ?: ""

        val content = """
            <h1 class="eb-page-title">오늘 수업</h1>
            ${StatTiles.render(dashboard.summary)}
            <div class="eb-today-layout">
                ${TodayClassesPanel.render(academyId, dashboard.classes)}
                ${AttendanceFeedPanel.render(events, amendedEventIds)}
            </div>
            $deliveryHtml
        """.trimIndent()

        DashboardShell.render(session, academyId, "today", content) {
            TodayClassesPanel.wireSelectAll()
            AttendanceFeedPanel.wireWithin(document, events, onCancelRequested(session, academyId))
            (document.getElementById("mark-attendance") as? HTMLButtonElement)?.onclick = {
                val selectedClassIds = TodayClassesPanel.selectedClassIds()
                if (selectedClassIds.isEmpty()) {
                    Toast.error("출석 처리할 수업을 선택해 주세요.")
                } else {
                    // 여러 수업을 선택해도 첫 수업부터 처리한다 — 명시적 배치 우선순위 없이는 이 이상의
                    // 다중 수업 동시 처리는 사용자가 무엇을 저장하는지 모호해진다(로스터 모달은 수업 1개 기준).
                    val classId = selectedClassIds.first()
                    val className = dashboard.classes.firstOrNull { it.classId == classId }?.className ?: classId
                    AttendanceModal.openRoster(session, academyId, classId, className, dashboard.serviceDate) {
                        open(session, academyId)
                    }
                }
                null
            }
        }
    }

    private const val FEED_LIMIT = 20
}
